import { HttpParams } from '@angular/common/http';

import { toHTTPParam } from '../../../core';
import { Periodicidade, periodicidadeMensal, isPeriodicidadePeriodo, isPeriodicidadeTodoPeriodo } from '../../../shared';
import { perspectivaTodos } from '../perspectiva';
import { TituloReceberReferenciaData, referenciaDataEmissao } from '../referencia-data';
import { Filter } from '../../../models';
import { Pessoa } from '../../pessoa';
import { Representante } from '../../pessoa/representante';

/** Representa o Filter da entidade TituloReceber */
export class TituloReceberFilter implements Filter {
    isFilterDirty = false;

    perspectiva: number;
    agruparPorCliente = false;

    // Data
    referenciaData: number;
    periodicidadeVigencia: number;
    dataInicioVigencia: Date;
    dataTerminoVigencia: Date;
    dataInicioVigenciaPeriodo: Date;
    dataTerminoVigenciaPeriodo: Date;
    mesVigencia: number;
    trimestreVigencia: number;
    semestreVigencia: number;
    anoVigencia: number;

    numeroErp: string;
    situacoes: number[] = [];
    idsPessoas: number[];
    pessoa: Pessoa;
    considerarSomenteTitulosRepresentante: boolean;
    idRepresentante: number;
    representante: Representante;
    idsRepresentantes: number[];
    somenteRepresentantesAtivos = true;
    idsGerentes: number[];
    idsAtendentes: number[];
    idsLocaisFaturamentos: number[] = [];
    idsClassificacoesClientes: number[] = [];
    classificacaoClienteNull = false;
    idsClassificacoesItens: number[] = [];
    classificacaoItemNull = false;

    constructor(periodicidadeVigencia: Periodicidade = periodicidadeMensal, referenciaData: TituloReceberReferenciaData = referenciaDataEmissao) {
        this.perspectiva = perspectivaTodos.id;
        this.referenciaData = referenciaData.id;
        this.periodicidadeVigencia = periodicidadeVigencia ? periodicidadeVigencia.id : null;
    }

    createParams(params: HttpParams): HttpParams {
        if (!params) {
            params = new HttpParams();
        }

        // Perspectiva
        if (this.perspectiva) {
            params = params.append('perspectiva', this.perspectiva.toString());
        }

        // Referência da data
        if (this.referenciaData) {
            params = params.append('referenciaData', this.referenciaData.toString());
        }

        // Periodicidade Vigência
        if (this.periodicidadeVigencia) {
            params = params.append('periodicidadeVigencia', this.periodicidadeVigencia.toString());
        }

        if (isPeriodicidadePeriodo(this.periodicidadeVigencia)) {
            // Data Início Vigência
            if (this.dataInicioVigenciaPeriodo) {
                params = params.append('dataInicioVigencia', toHTTPParam(this.dataInicioVigenciaPeriodo));
            }
            // Data Término Vigência
            if (this.dataTerminoVigenciaPeriodo) {
                params = params.append('dataTerminoVigencia', toHTTPParam(this.dataTerminoVigenciaPeriodo));
            }
        } else {
            // Data Início Vigência
            if (this.dataInicioVigencia) {
                params = params.append('dataInicioVigencia', toHTTPParam(this.dataInicioVigencia));
            }
            // Data Término Vigência
            if (this.dataTerminoVigencia) {
                params = params.append('dataTerminoVigencia', toHTTPParam(this.dataTerminoVigencia));
            }
        }

        // Número ERP
        if (this.numeroErp) {
            params = params.append('numeroErp', this.numeroErp);
        }

        // Situações
        this.situacoes?.forEach(situacao => {
            params = params.append('situacao', situacao.toString());
        });

        // Pessoa
        if (this.idsPessoas) {
            this.idsPessoas.forEach(idPessoa => {
                params = params.append('idPessoa', idPessoa.toString());
            });
        }

        // Se deve considerar somente títulos vinculados aos representantes filtrados
        if (this.considerarSomenteTitulosRepresentante != null && this.considerarSomenteTitulosRepresentante !== undefined) {
            params = params.append('considerarSomenteTitulosRepresentante', this.considerarSomenteTitulosRepresentante.toString());
        }
        // Representante
        if (this.idRepresentante) {
            params = params.append('idRepresentante', this.idRepresentante.toString());
        }
        if (this.idsRepresentantes && this.idsRepresentantes.length > 0) {
            this.idsRepresentantes.forEach(id => {
                params = params.append('idRepresentante', id.toString());
            });
        }
        // Gerentes
        if (this.idsGerentes && this.idsGerentes.length > 0) {
            this.idsGerentes.forEach(id => {
                params = params.append('idsGerentes', id.toString());
            });
        }
        // Atendentes
        if (this.idsAtendentes && this.idsAtendentes.length > 0) {
            this.idsAtendentes.forEach(id => {
                params = params.append('idsAtendentes', id.toString());
            });
        }
        // Local de Faturamento
        this.idsLocaisFaturamentos?.forEach(idLocalFaturamento => {
            params = params.append('idLocalFaturamento', idLocalFaturamento.toString());
        });

        // Classificações de clientes
        this.idsClassificacoesClientes?.forEach(idClassificacaoCliente => {
            if (idClassificacaoCliente > 0) {
                params = params.append('idClassificacaoCliente', idClassificacaoCliente.toString());
            }
        });
        // Sem classificação de cliente
        if (this.classificacaoClienteNull) {
            params = params.append('classificacaoClienteNull', 'true');
        }

        // Classificações de itens
        this.idsClassificacoesItens?.forEach(idClassificacaoItem => {
            if (idClassificacaoItem > 0) {
                params = params.append('idClassificacaoItem', idClassificacaoItem.toString());
            }
        });
        // Sem classificação de item
        if (this.classificacaoItemNull) {
            params = params.append('classificacaoItemNull', 'true');
        }


        return params;
    }

    clear() {
        this.isFilterDirty = false;
        this.numeroErp = null;
        this.situacoes = [];
        this.idsPessoas = [];
        this.pessoa = null;
        this.considerarSomenteTitulosRepresentante = false;
        this.idRepresentante = null;
        this.representante = null;
        this.idsRepresentantes = [];
        this.somenteRepresentantesAtivos = true;
        this.idsGerentes = [];
        this.idsAtendentes = [];
        this.idsLocaisFaturamentos = [];
        this.idsClassificacoesClientes = [];
        this.classificacaoClienteNull = false;
        this.idsClassificacoesItens = [];
        this.classificacaoItemNull = false;
    }

    /** Verifica se a periodicidade é por todo periodo */
    isPeriodicidadeTodoPeriodo(): boolean {
        return isPeriodicidadeTodoPeriodo(this.periodicidadeVigencia);
    }

    /** Verificar se o filtro possui a periodicidade da vigência considerando a periodicidade e data de início da vigência */
    possuiPeriodicidadeVigencia(): boolean {
        // Periodicidade Vigência
        if (!this.periodicidadeVigencia) {
            return false;
        }
        // Data Início Vigência
        if (!this.dataInicioVigencia) {
            return false;
        }

        return true;
    }
}

